import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { ShieldAlert, Waves, Plus, CheckCircle2, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";

const LANES = [
  { key: "not_covered", label: "Not Covered", hint: "No adaptation plan yet", accent: "border-red-500/50", dot: "bg-red-500" },
  { key: "planned", label: "Planned", hint: "Draft / proposed / approved", accent: "border-[#A78BFA]/50", dot: "bg-[#A78BFA]" },
  { key: "in_progress", label: "In Progress", hint: "Being delivered", accent: "border-[#FFB800]/50", dot: "bg-[#FFB800]" },
  { key: "delivered", label: "Delivered", hint: "All plans completed", accent: "border-[#00E65B]/50", dot: "bg-[#00E65B]" },
];
const BAND = { critical: "text-red-500 border-red-500/40", high: "text-orange-500 border-orange-500/40", medium: "text-[#FFB800] border-[#FFB800]/40", low: "text-muted-foreground" };
const RAG_BG = { green: "bg-[#00E65B]", amber: "bg-[#FFB800]", red: "bg-red-500" };

function Summary({ s }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="adapt-summary">
      <div className="card-flat p-4">
        <div className="label-eyebrow text-[10px] text-muted-foreground mb-1">Coverage</div>
        <div className="font-mono text-2xl font-bold" data-testid="adapt-coverage-pct">{s.coverage_pct}<span className="text-xs text-muted-foreground ml-1">%</span></div>
        <Progress value={s.coverage_pct} className="h-1 mt-2" />
      </div>
      <div className="card-flat p-4"><div className="label-eyebrow text-[10px] text-muted-foreground mb-1">Physical risks</div><div className="font-mono text-2xl font-bold">{s.total_physical_risks}</div></div>
      <div className="card-flat p-4"><div className="label-eyebrow text-[10px] text-muted-foreground mb-1">Uncovered</div><div className={`font-mono text-2xl font-bold ${s.uncovered ? "text-red-500" : "text-[#00E65B]"}`}>{s.uncovered}</div></div>
      <div className="card-flat p-4"><div className="label-eyebrow text-[10px] text-muted-foreground mb-1">In progress</div><div className="font-mono text-2xl font-bold text-[#FFB800]">{s.in_progress}</div></div>
      <div className="card-flat p-4"><div className="label-eyebrow text-[10px] text-muted-foreground mb-1">Adaptation initiatives</div><div className="font-mono text-2xl font-bold">{s.adaptation_total}</div></div>
    </div>
  );
}

function RiskCard({ c, onOpenInitiative, onPlan, planning }) {
  return (
    <div className="card-flat p-3" data-testid={`adapt-risk-${c.risk_id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium leading-tight">{c.title}</span>
        <Badge variant="outline" className={`text-[9px] shrink-0 ${BAND[c.residual_band] || ""}`}>{c.residual_band}</Badge>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
        <span>{c.owner || "Unassigned"}</span>
        {c.time_horizon && <span>· {c.time_horizon}</span>}
        <span className="ml-auto font-mono">P {c.priority_score}</span>
      </div>

      {c.initiative_count > 0 ? (
        <div className="mt-2 space-y-1.5">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground"><span>{c.initiative_count} adaptation plan{c.initiative_count > 1 ? "s" : ""}</span><span className="font-mono">{c.avg_progress}%</span></div>
          <Progress value={c.avg_progress} className="h-1" />
          {c.initiatives.map((i) => (
            <button key={i.id} onClick={() => onOpenInitiative && onOpenInitiative(i.id)} data-testid={`adapt-init-${i.id}`}
              className="w-full text-left flex items-center gap-1.5 text-[11px] hover:text-accent transition-colors">
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${RAG_BG[i.rag_status] || "bg-muted"}`} />
              <span className="truncate flex-1">{i.name}</span>
              <span className="text-[10px] text-muted-foreground font-mono">{i.progress_percent}%</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-[11px] text-red-400/90 mb-2">No adaptation initiative is treating this physical-climate risk.</p>
          <Button size="sm" variant="outline" className="w-full h-7 text-xs" disabled={planning === c.risk_id}
            onClick={() => onPlan(c)} data-testid={`adapt-plan-${c.risk_id}`}>
            {planning === c.risk_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Plan adaptation
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AdaptationReadinessTab({ onOpenInitiative, onChanged }) {
  const [data, setData] = useState(null);
  const [planning, setPlanning] = useState(null);

  const load = useCallback(() => {
    api.get("/initiatives/adaptation-readiness").then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); setData({ empty: true }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const planAdaptation = async (c) => {
    setPlanning(c.risk_id);
    try {
      const r = await api.post("/initiatives/create", {
        initiative_name: `Adaptation: ${c.title}`, initiative_type: "risk_mitigation",
        climate_response: "adaptation", linked_risk: c.title, linked_risk_id: c.risk_id,
        priority: c.residual_band === "critical" || c.residual_band === "high" ? "high" : "medium", status: "draft",
      });
      toast.success("Adaptation plan created");
      load(); onChanged && onChanged();
      onOpenInitiative && onOpenInitiative(r.data.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setPlanning(null); }
  };

  if (!data) return <div className="py-10 text-center text-muted-foreground text-sm">Loading adaptation readiness…</div>;
  if (data.empty) {
    return (
      <div className="card-flat p-10 text-center">
        <Waves className="h-8 w-8 text-accent mx-auto mb-3" />
        <h3 className="font-display text-xl font-bold">No physical-climate risks yet</h3>
        <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">Add Climate Physical risks (flood, heat stress, water scarcity) in the Risk Register, then tag adaptation initiatives to see coverage here.</p>
      </div>
    );
  }

  const { summary, lanes, unlinked_initiatives } = data;
  return (
    <div className="space-y-5" data-testid="adapt-readiness-tab">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-6 w-6 text-accent mt-0.5 shrink-0" />
        <div>
          <h3 className="font-display text-lg font-bold">Climate Adaptation Readiness</h3>
          <p className="text-muted-foreground text-sm">How well your top physical-climate risks are covered by adaptation initiatives — aligned to Singapore's 2026 Year of Climate Adaptation.</p>
        </div>
      </div>

      <Summary s={summary} />

      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="adapt-board">
        {LANES.map((lane) => {
          const cards = lanes[lane.key] || [];
          return (
            <div key={lane.key} className="min-w-[260px] flex-1" data-testid={`adapt-lane-${lane.key}`}>
              <div className={`flex items-center justify-between mb-2 px-2 py-2 rounded-sm border-l-2 bg-surface ${lane.accent}`}>
                <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${lane.dot}`} /><span className="text-sm font-medium">{lane.label}</span></div>
                <span className="text-xs font-mono text-muted-foreground" data-testid={`adapt-lane-count-${lane.key}`}>{cards.length}</span>
              </div>
              <div className="text-[10px] text-muted-foreground px-2 mb-2">{lane.hint}</div>
              <div className="space-y-2">
                {cards.map((c) => <RiskCard key={c.risk_id} c={c} onOpenInitiative={onOpenInitiative} onPlan={planAdaptation} planning={planning} />)}
                {!cards.length && <div className="text-[11px] text-muted-foreground text-center py-4 border border-dashed border-border rounded-sm">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {(unlinked_initiatives || []).length > 0 && (
        <div className="card-flat p-4" data-testid="adapt-unlinked">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Adaptation initiatives not linked to a physical risk ({unlinked_initiatives.length})</div>
          <p className="text-xs text-muted-foreground mb-3">Open an initiative and set its “Linked risk” to a Climate Physical risk so it counts toward coverage above.</p>
          <div className="flex flex-wrap gap-2">
            {unlinked_initiatives.map((i) => (
              <button key={i.id} onClick={() => onOpenInitiative && onOpenInitiative(i.id)} data-testid={`adapt-unlinked-${i.id}`}
                className="inline-flex items-center gap-1.5 text-xs border border-border rounded-sm px-2.5 py-1.5 hover:border-accent/60 transition-colors">
                {i.status === "completed" ? <CheckCircle2 className="h-3 w-3 text-[#00E65B]" /> : <span className={`h-1.5 w-1.5 rounded-full ${RAG_BG[i.rag_status] || "bg-muted"}`} />}
                <span>{i.name}</span><span className="text-[10px] text-muted-foreground font-mono">{i.progress_percent}%</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
